import { StaticSubscriber } from '@zinnia/form-engine-sdk';

import { NodeIds } from '@breathelife/insurance-form-builder';

import { useLookupAdvisorMutation } from '../../../ReactQuery/AgentLookup/agentLookup.mutations';

type AdvisorSlot = {
  buttonNodeId: string;
  codeNodeId: string;
  fullNameNodeId: string;
};

// Mirrors the agent code field's own content-configured length validator; the engine doesn't expose per-field
// validity to a StaticSubscriber, so this is duplicated here to avoid calling the API with an incomplete code.
const AGENT_CODE_MIN_LENGTH = 6;

const advisorSlots: AdvisorSlot[] = [
  {
    buttonNodeId: NodeIds.producerReportSecondInsuranceProducerValidateCode,
    codeNodeId: NodeIds.producerReportSecondAgentCode,
    fullNameNodeId: NodeIds.producerReportSecondInsuranceProducerFullName,
  },
  {
    buttonNodeId: NodeIds.producerReportThirdInsuranceProducerValidateCode,
    codeNodeId: NodeIds.producerReportThirdAgentCode,
    fullNameNodeId: NodeIds.producerReportThirdInsuranceProducerFullName,
  },
];

export const createAgentLookupSubscriber = (
  lookupAdvisor: ReturnType<typeof useLookupAdvisorMutation>['mutateAsync'],
  setIsActive: (value: boolean) => void,
): StaticSubscriber => {
  // Local state, scoped to this subscriber instance (recreated along with the questionnaire engine): tracks the agent
  // code that was last successfully looked up per button, so a repeat click with the same code skips calling the API
  // again. Cleared as soon as the code itself changes, so editing the code always allows a fresh lookup.
  const lastValidatedCodeBySlot = new Map<string, string>();

  return async (questionnaireEngine, nodeIds) => {
    for (const slot of advisorSlots) {
      if (!nodeIds.includes(slot.codeNodeId)) continue;

      const currentCode = questionnaireEngine.getAnswer(slot.codeNodeId, {}) as string | undefined;
      if (lastValidatedCodeBySlot.get(slot.buttonNodeId) === currentCode) continue;

      lastValidatedCodeBySlot.delete(slot.buttonNodeId);
      if (questionnaireEngine.getAnswer(slot.fullNameNodeId, {})) {
        questionnaireEngine.updateAnswer({ tag: 'nodeId', nodeId: slot.fullNameNodeId, value: '', nodeIdScope: {} });
      }
    }

    const triggeredSlots = advisorSlots.filter(
      (slot) => nodeIds.includes(slot.buttonNodeId) && questionnaireEngine.getAnswer(slot.buttonNodeId, {}) === true,
    );

    if (!triggeredSlots.length) {
      return;
    }

    setIsActive(true);

    try {
      for (const slot of triggeredSlots) {
        // Reset the button's own click-answer immediately: this field has no "unclick" action, so without this the
        // answer stays `true` and a retry (true -> true) is a no-op the engine won't emit as a change.
        questionnaireEngine.updateAnswer({ tag: 'nodeId', nodeId: slot.buttonNodeId, value: false, nodeIdScope: {} });

        const agentCode = questionnaireEngine.getAnswer(slot.codeNodeId, {}) as string | undefined;
        if (
          !agentCode ||
          agentCode.length < AGENT_CODE_MIN_LENGTH ||
          lastValidatedCodeBySlot.get(slot.buttonNodeId) === agentCode
        ) {
          continue;
        }

        try {
          const result = await lookupAdvisor(agentCode);

          if (result.status === 'found') {
            lastValidatedCodeBySlot.set(slot.buttonNodeId, agentCode);
            questionnaireEngine.updateAnswer({
              tag: 'nodeId',
              nodeId: slot.fullNameNodeId,
              value: result.fullName,
              nodeIdScope: {},
            });
          }
        } catch {
          // Notification is already dispatched by useLookupAdvisorMutation's onError.
        }
      }
    } finally {
      setIsActive(false);
    }
  };
};
